<template>
  <div class="audit-report-panel">
    <div class="report-search-box">
      <div class="report-sample-tips">
        <strong>示例：</strong>
        <el-tag
          v-for="s in sampleQueries"
          :key="s.query"
          size="small"
          class="report-sample"
          @click="fillReportSample(s)"
          >{{ s.label }}</el-tag
        >
      </div>
      <div class="report-input-row">
        <el-input
          v-model="reportQuery"
          placeholder="输入查询语句，例如：今天客服审核实际承运人的情况"
          clearable
          class="report-query-input"
          @keyup.enter.native="doSmartReportQuery"
        />
        <el-button
          type="primary"
          class="report-query-btn"
          :loading="reportLoading"
          @click="doSmartReportQuery"
          >智能查询</el-button
        >
        <el-button
          type="success"
          class="report-export-btn"
          :disabled="reportExportDisabled"
          @click="exportReport"
          >导出CSV</el-button
        >
      </div>
      <div class="report-time-row">
        <label class="report-time-label">自定义时间范围：</label>
        <el-date-picker
          v-model="reportTimeRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          format="yyyy-MM-dd HH:mm:ss"
          value-format="yyyy-MM-dd HH:mm:ss"
          :picker-options="pickerOptions"
          style="width: 380px"
        />
        <el-button
          class="report-time-query-btn"
          :loading="reportLoading"
          @click="doTimeRangeQuery"
          >时间查询</el-button
        >
        <el-button class="report-time-clear-btn" @click="clearReportTime"
          >清空</el-button
        >
      </div>
    </div>

    <div v-if="reportParsed" class="report-parse-tip">
      解析结果：<strong>{{ reportParsed.timeLabel }}</strong>
      <span v-if="reportParsed.timeRange && reportParsed.timeRange.start">
        （{{ reportParsed.timeRange.start }} ~
        {{ reportParsed.timeRange.end }}） </span
      >，统计对象：<strong>{{ reportTargetLabel }}</strong>
    </div>

    <div v-loading="reportLoading" class="report-result-area">
      <el-empty
        v-if="!reportLoading && !reportData.length"
        :description="reportEmptyText"
      />
      <template v-if="!reportLoading && reportData.length">
        <div class="report-summary">
          <div class="report-summary-item">
            <div class="report-summary-label">审核人数</div>
            <div class="report-summary-value">{{ reportUserCount }}</div>
          </div>
          <div class="report-summary-item">
            <div class="report-summary-label">审核总数</div>
            <div class="report-summary-value">{{ reportTotalCount }}</div>
          </div>
          <div class="report-summary-item">
            <div class="report-summary-label">人均审核</div>
            <div class="report-summary-value">{{ reportAvgCount }}</div>
          </div>
        </div>
        <div class="report-flex-row">
          <div class="report-chart-box">
            <svg
              ref="reportChart"
              class="report-chart"
              width="600"
              height="380"
            ></svg>
          </div>
          <div class="report-table-box">
            <el-table
              :data="reportData"
              class="report-table"
              size="mini"
              max-height="400"
              style="width: 100%"
            >
              <el-table-column type="index" label="序号" width="60" />
              <el-table-column
                prop="authPerson"
                label="审核人"
                min-width="120"
              />
              <!-- 综合模式：仅展示查询实际命中的对象列（与原型一致） -->
              <el-table-column
                v-for="part in reportTypeParts"
                :key="part"
                :label="targetLabel(part)"
                :width="targetColumnWidth(part)"
                align="right"
              >
                <template slot-scope="scope">{{
                  scope.row[typeFieldMap[part]] || 0
                }}</template>
              </el-table-column>
              <el-table-column
                v-if="reportIsAll"
                prop="reviewCount"
                label="合计"
                width="80"
                align="right"
              />
              <el-table-column
                v-else
                prop="reviewCount"
                label="审核数量"
                width="110"
                align="right"
              />
              <el-table-column label="时间段" width="120" align="center">
                <template>{{
                  reportParsed ? reportParsed.timeLabel : ""
                }}</template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { smartReport, reportExport } from "@/api/smartAssistant";
import { downloadBlob } from "../utils";

// 对象词 -> 后端返回字段（后端历史返回"司机"，产品统一显示为"实际承运人"，两种 token 均需兼容）
const TYPE_FIELD_MAP = {
  实际承运人: "driverCount",
  司机: "driverCount",
  车辆: "vehicleCount",
  运单: "orderCount"
};

// 后端对象词 -> 前端展示名（把"司机"统一映射为"实际承运人"）
const TARGET_LABEL_MAP = {
  司机: "实际承运人",
  实际承运人: "实际承运人",
  车辆: "车辆",
  运单: "运单"
};

// 单对象柱状图调色板（与原型 drawReportBarChart 一致）
const BAR_COLORS = [
  "#4285f4",
  "#34a853",
  "#fbbc05",
  "#ea4335",
  "#9334e6",
  "#00acc1",
  "#ef6c00",
  "#795548",
  "#607d8b",
  "#e91e63",
  "#00bcd4",
  "#8bc34a",
  "#ff9800",
  "#ff5722",
  "#673ab7"
];

export default {
  name: "AuditReportPanel",
  data() {
    return {
      reportQuery: "",
      reportTimeRange: null,
      reportLoading: false,
      reportData: [],
      reportParsed: null,
      reportIsAll: false,
      reportTotalCount: 0,
      reportUserCount: 0,
      reportAvgCount: 0,
      reportExportDisabled: true,
      // 是否已执行过查询（区分"尚未查询"与"查询无数据"两种空态）
      hasQueried: false,
      typeFieldMap: TYPE_FIELD_MAP,
      // 展示标签与真实查询语句分离（与原型一致："本月综合统计"发送完整长句触发三对象综合统计）
      sampleQueries: [
        {
          label: "今天客服审核实际承运人的情况",
          query: "今天客服审核实际承运人的情况"
        },
        { label: "我想看这周车辆审核情况", query: "我想看这周车辆审核情况" },
        {
          label: "上月每个客服审核了多少运单",
          query: "上月每个客服审核了多少运单"
        },
        { label: "近7天实际承运人审核量", query: "近7天实际承运人审核量" },
        {
          label: "本月综合统计",
          query: "这个月每个客服分别审核了多少实际承运人、车辆和运单"
        }
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: "今天",
            onClick: picker => picker.$emit("pick", [new Date(), new Date()])
          },
          {
            text: "本周",
            onClick: picker => picker.$emit("pick", this.weekRange())
          },
          {
            text: "本月",
            onClick: picker => picker.$emit("pick", this.monthRange())
          },
          {
            text: "上月",
            onClick: picker => picker.$emit("pick", this.lastMonthRange())
          }
        ]
      }
    };
  },
  computed: {
    // 综合模式下实际查询的对象（实际承运人/车辆/运单），用于动态列表头
    reportTypeParts() {
      if (this.reportIsAll && this.reportParsed && this.reportParsed.target) {
        return this.reportParsed.target.split("+");
      }
      return [];
    },
    reportEmptyText() {
      return this.hasQueried
        ? "暂无数据"
        : "请输入查询语句后点击“智能查询”按钮";
    },
    // 统计对象展示名（把后端返回的"司机"统一显示为"实际承运人"）
    reportTargetLabel() {
      const target = this.reportParsed && this.reportParsed.target;
      if (!target) {
        return "";
      }
      return target
        .split("+")
        .map(part => TARGET_LABEL_MAP[part] || part)
        .join("+");
    }
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    fillReportSample(sample) {
      this.reportQuery = sample.query;
      this.doSmartReportQuery();
    },
    // 后端对象词 -> 展示名（"司机" -> "实际承运人"）
    targetLabel(part) {
      return TARGET_LABEL_MAP[part] || part;
    },
    // 动态对象列宽：实际承运人较长需加宽，避免表头换行
    targetColumnWidth(part) {
      const label = this.targetLabel(part);
      return Math.max(70, label.length * 18 + 8);
    },
    // 将时间范围转为接口参数，未选择时间时返回 null
    getReportTimeParams() {
      if (this.reportTimeRange && this.reportTimeRange.length === 2) {
        return {
          start_date: this.reportTimeRange[0],
          end_date: this.reportTimeRange[1]
        };
      }
      return null;
    },
    doSmartReportQuery() {
      const params = this.getReportTimeParams() || {};
      if (!this.reportQuery && !params.start_date) {
        this.$message.warning("请输入查询语句或选择时间范围");
        return;
      }
      if (this.reportQuery) {
        params.q = this.reportQuery;
      }
      this.runSmartReport(params);
    },
    doTimeRangeQuery() {
      const params = this.getReportTimeParams();
      if (!params) {
        this.$message.warning("请选择时间范围");
        return;
      }
      this.runSmartReport(params);
    },
    clearReportTime() {
      // 与原型一致：仅清空自定义时间范围，不清除查询词与查询结果
      this.reportTimeRange = null;
    },
    runSmartReport(params) {
      this.reportLoading = true;
      smartReport(params)
        .then(res => {
          const body = res.data || {};
          this.reportParsed = body.parsed || null;
          this.reportData = body.data || [];
          this.reportTotalCount = body.totalCount || 0;
          this.reportIsAll = !!(
            this.reportParsed &&
            this.reportParsed.target &&
            this.reportParsed.target.indexOf("+") !== -1
          );
          this.reportUserCount = this.reportData.length;
          this.reportAvgCount = this.reportData.length
            ? Math.round(this.reportTotalCount / this.reportData.length)
            : 0;
          this.reportExportDisabled = false;
          this.hasQueried = true;
        })
        .catch(() => {
          // 错误提示已由 request 拦截器统一处理
        })
        .finally(() => {
          this.reportLoading = false;
          // 必须等 loading 关闭、图表容器挂载后再初始化，否则 $refs 为空导致柱状图不显示
          this.$nextTick(() => {
            this.drawReportChart();
          });
        });
    },
    // 与原型 other/index.html drawReportBarChart 完全一致的 SVG 柱状图（600x380）
    drawReportChart() {
      const svg = this.$refs.reportChart;
      if (!svg) return;
      const svgNS = "http://www.w3.org/2000/svg";
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      const width = 600;
      const height = 380;
      const paddingL = 70;
      const paddingR = 20;
      const paddingT = 30;
      const paddingB = 70;
      const chartW = width - paddingL - paddingR;
      const chartH = height - paddingT - paddingB;
      const ySteps = 5;

      const data = this.reportData || [];
      const isAll = this.reportIsAll;
      const parts =
        isAll && this.reportParsed ? this.reportParsed.target.split("+") : [];

      const appendText = (x, y, content, opts) => {
        const o = opts || {};
        const text = document.createElementNS(svgNS, "text");
        text.setAttribute("x", x);
        text.setAttribute("y", y);
        text.setAttribute("text-anchor", o.anchor || "middle");
        text.setAttribute("fill", o.fill || "#555");
        text.setAttribute("font-size", o.size || 11);
        if (o.weight) text.setAttribute("font-weight", o.weight);
        if (o.rotate)
          text.setAttribute("transform", `rotate(${o.rotate}, ${x}, ${y})`);
        text.textContent = content;
        svg.appendChild(text);
        return text;
      };
      const appendLine = (x1, y1, x2, y2, stroke) => {
        const line = document.createElementNS(svgNS, "line");
        line.setAttribute("x1", x1);
        line.setAttribute("y1", y1);
        line.setAttribute("x2", x2);
        line.setAttribute("y2", y2);
        line.setAttribute("stroke", stroke);
        line.setAttribute("stroke-width", "1");
        svg.appendChild(line);
      };
      const shortName = name => {
        const n = name || "未知";
        return n.length > 5 ? n.slice(0, 5) + "…" : n;
      };
      const labelOf = part => TARGET_LABEL_MAP[part] || part;

      if (!data.length) {
        appendText(width / 2, height / 2, "暂无数据", {
          fill: "#999",
          size: 14
        });
        return;
      }

      const fieldMap = {
        司机: { key: "driverCount", color: "#4285f4" },
        实际承运人: { key: "driverCount", color: "#4285f4" },
        车辆: { key: "vehicleCount", color: "#34a853" },
        运单: { key: "orderCount", color: "#fbbc05" }
      };

      // ---------- 综合模式：动态分组柱状图 ----------
      if (isAll) {
        const displayData = data.slice(0, 12);
        const maxVal = Math.max(
          ...displayData.map(d =>
            parts.reduce((m, p) => Math.max(m, d[fieldMap[p].key] || 0), 0)
          ),
          1
        );
        const niceMax = Math.ceil(maxVal / 5) * 5 || maxVal;
        const groupGap = 8;
        const barW = parts.length === 1 ? 40 : 14;
        const groupW = barW * parts.length + (parts.length - 1) * 2;
        const totalW = displayData.length * (groupW + groupGap);
        const startX = paddingL + Math.max(0, (chartW - totalW) / 2);

        for (let i = 0; i <= ySteps; i++) {
          const y = paddingT + chartH - (i / ySteps) * chartH;
          appendLine(paddingL, y, width - paddingR, y, "#eee");
          appendText(paddingL - 8, y + 4, Math.round((i / ySteps) * niceMax), {
            anchor: "end",
            fill: "#999",
            size: 11
          });
        }

        displayData.forEach((item, idx) => {
          const gx = startX + idx * (groupW + groupGap);
          parts.forEach((p, bi) => {
            const f = fieldMap[p];
            const val = item[f.key] || 0;
            const barH = (val / niceMax) * chartH;
            const y = paddingT + chartH - barH;
            const x = gx + bi * (barW + 2);
            const rect = document.createElementNS(svgNS, "rect");
            rect.setAttribute("x", x);
            rect.setAttribute("y", y);
            rect.setAttribute("width", barW);
            rect.setAttribute("height", barH);
            rect.setAttribute("fill", f.color);
            rect.setAttribute("rx", "2");
            rect.setAttribute("opacity", "0.88");
            svg.appendChild(rect);
            if (val > 0 && parts.length <= 2) {
              appendText(x + barW / 2, y - 4, val, { fill: "#555", size: 9 });
            }
          });
          appendText(
            gx + groupW / 2,
            paddingT + chartH + 16,
            shortName(item.authPerson),
            { fill: "#555", size: 10, rotate: -30 }
          );
        });

        appendLine(
          paddingL,
          paddingT + chartH,
          width - paddingR,
          paddingT + chartH,
          "#ccc"
        );
        appendText(width / 2, 16, "审核统计（" + parts.map(labelOf).join("/") + "）Top 12", {
          fill: "#333",
          size: 13,
          weight: 600
        });

        if (parts.length > 1) {
          const legendY = height - 8;
          parts.forEach((p, li) => {
            const lx = paddingL + li * 80;
            const sw = document.createElementNS(svgNS, "rect");
            sw.setAttribute("x", lx);
            sw.setAttribute("y", legendY - 10);
            sw.setAttribute("width", 12);
            sw.setAttribute("height", 12);
            sw.setAttribute("fill", fieldMap[p].color);
            sw.setAttribute("rx", "2");
            svg.appendChild(sw);
            appendText(lx + 16, legendY, labelOf(p), {
              anchor: "start",
              fill: "#555",
              size: 11
            });
          });
        }
        return;
      }

      // ---------- 单一模式柱状图 ----------
      const displayData = data.slice(0, 15);
      const maxVal = Math.max(...displayData.map(d => d.reviewCount || 0), 1);
      const niceMax = Math.ceil(maxVal / 5) * 5 || maxVal;
      const barGap = 10;
      const barW = Math.max(
        20,
        Math.min(
          60,
          (chartW - barGap * (displayData.length + 1)) / displayData.length
        )
      );

      for (let i = 0; i <= ySteps; i++) {
        const y = paddingT + chartH - (i / ySteps) * chartH;
        appendLine(paddingL, y, width - paddingR, y, "#eee");
        appendText(paddingL - 8, y + 4, Math.round((i / ySteps) * niceMax), {
          anchor: "end",
          fill: "#999",
          size: 11
        });
      }

      displayData.forEach((item, idx) => {
        const x = paddingL + barGap + idx * (barW + barGap);
        const barH = ((item.reviewCount || 0) / niceMax) * chartH;
        const y = paddingT + chartH - barH;
        const rect = document.createElementNS(svgNS, "rect");
        rect.setAttribute("x", x);
        rect.setAttribute("y", y);
        rect.setAttribute("width", barW);
        rect.setAttribute("height", barH);
        rect.setAttribute("fill", BAR_COLORS[idx % BAR_COLORS.length]);
        rect.setAttribute("rx", "4");
        rect.setAttribute("opacity", "0.9");
        svg.appendChild(rect);
        appendText(x + barW / 2, y - 6, item.reviewCount || 0, {
          fill: "#333",
          size: 12,
          weight: 600
        });
        appendText(
          x + barW / 2,
          paddingT + chartH + 18,
          shortName(item.authPerson),
          { fill: "#555", size: 11, rotate: -30 }
        );
      });

      appendLine(
        paddingL,
        paddingT + chartH,
        width - paddingR,
        paddingT + chartH,
        "#ccc"
      );
      appendText(width / 2, 18, "审核人审核数量统计（Top 15）", {
        fill: "#333",
        size: 14,
        weight: 600
      });
    },
    destroyChart() {
      const svg = this.$refs.reportChart;
      if (svg) {
        while (svg.firstChild) svg.removeChild(svg.firstChild);
      }
    },
    exportReport() {
      const params = this.getReportTimeParams() || {};
      if (this.reportQuery) {
        params.q = this.reportQuery;
      }
      reportExport(params)
        .then(blob => {
          downloadBlob(blob, this.buildReportExportName());
        })
        .catch(() => {
          this.$message.error("导出失败，请稍后重试");
        });
    },
    // 与原型一致的导出文件名：report_{target}_{timeType}_{yyyyMMddHHmmss}.csv
    buildReportExportName() {
      const parsed = this.reportParsed;
      let target = "report";
      if (parsed && parsed.target) {
        const t = parsed.target;
        if (t.indexOf("+") !== -1) {
          target = "all";
        } else if (t.indexOf("实际承运人") !== -1 || t.indexOf("司机") !== -1) {
          target = "driver";
        } else if (t.indexOf("车辆") !== -1) {
          target = "vehicle";
        } else if (t.indexOf("运单") !== -1) {
          target = "order";
        }
      }
      const timeType = (parsed && parsed.timeType) || "manual";
      return `report_${target}_${timeType}_${this.formatTimestamp(
        new Date()
      )}.csv`;
    },
    formatTimestamp(d) {
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
        d.getDate()
      )}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    },
    weekRange() {
      const now = new Date();
      const day = now.getDay() === 0 ? 7 : now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - (day - 1));
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      return [monday, sunday];
    },
    monthRange() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      return [start, end];
    },
    lastMonthRange() {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        0,
        23,
        59,
        59,
        999
      );
      return [start, end];
    }
  }
};
</script>

<style lang="scss" scoped>
/* 样式对齐原型 other/index.html 的审核报表（.report-*） */
.audit-report-panel {
  .report-search-box {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
  }
  .report-sample-tips {
    margin-bottom: 12px;
    font-size: 13px;
    color: #666;
    strong {
      color: #333;
      margin-right: 6px;
    }
  }
  /* 示例标签：原型为蓝色胶囊 */
  .report-sample {
    height: auto;
    line-height: 1.5;
    padding: 3px 10px;
    margin: 2px 4px;
    background: #e8f0fe;
    color: #4285f4;
    border: 1px solid #c6dafc;
    border-radius: 12px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    &:hover {
      background: #d2e3fc;
      border-color: #4285f4;
    }
  }
  .report-input-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    .report-query-input {
      flex: 1;
      max-width: none;
      ::v-deep .el-input__inner {
        font-size: 13px;
      }
    }
  }
  .report-time-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #666;
    .report-time-label {
      color: #555;
      white-space: nowrap;
      font-size: 13px;
    }
  }
  /* 按钮：原型蓝(主) + 浅绿(导出)，清空为文字链接 */
  .report-query-btn,
  .report-time-query-btn {
    background: #4285f4;
    border-color: #4285f4;
    color: #fff;
    &:hover,
    &:focus {
      background: #3367d6;
      border-color: #3367d6;
      color: #fff;
    }
  }
  .report-export-btn {
    background: #f0fdf4;
    color: #16a34a;
    border-color: #86efac;
    &:hover,
    &:focus {
      background: #16a34a;
      border-color: #16a34a;
      color: #fff;
    }
    &.is-disabled,
    &.is-disabled:hover {
      background: #f5f5f5;
      border-color: #e4e7ed;
      color: #c0c4cc;
    }
  }
  .report-time-clear-btn {
    border: none;
    background: none;
    color: #4285f4;
    padding: 0;
    font-size: 12px;
    &:hover,
    &:focus {
      border: none;
      background: none;
      color: #3367d6;
    }
  }
  .report-parse-tip {
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #e6f4ff;
    border: 1px solid #91caff;
    border-radius: 4px;
    font-size: 13px;
    color: #0958d9;
    strong {
      font-weight: 600;
    }
  }
  .report-result-area {
    position: relative;
  }
  /* 汇总卡片：三色渐变，白字 */
  .report-summary {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    .report-summary-item {
      flex: 1;
      padding: 16px 20px;
      border-radius: 8px;
      color: #fff;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      &:nth-child(2) {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      }
      &:nth-child(3) {
        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      }
    }
    .report-summary-label {
      font-size: 12px;
      opacity: 0.9;
      margin-bottom: 6px;
    }
    .report-summary-value {
      font-size: 24px;
      font-weight: 700;
    }
  }
  .report-flex-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    .report-chart-box {
      flex: 1;
      min-width: 500px;
      background: #fafafa;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 16px;
      .report-chart {
        display: block;
      }
    }
    .report-table-box {
      flex: 1;
      min-width: 400px;
      background: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      overflow: hidden;
    }
  }
  /* 空态：原型为虚线卡片 */
  ::v-deep .report-result-area .el-empty {
    padding: 60px 20px;
    background: #fafafa;
    border: 1px dashed #e0e0e0;
    border-radius: 8px;
  }
  /* 表格：原型表头浅灰底、13px、行悬浮高亮 */
  ::v-deep .report-table {
    font-size: 13px;
    th.el-table__cell {
      background: #f5f7fa;
      color: #333;
      font-weight: 600;
      padding: 10px 0;
      border-bottom: 2px solid #e8e8e8;
    }
    td.el-table__cell {
      padding: 10px 0;
      color: #555;
    }
    .el-table__body tr:hover > td.el-table__cell {
      background: #f8faff;
    }
  }
}
</style>
